import type { ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useTheme } from 'components/core/theme-provider';
import { cn } from 'lib/utils';

export interface ChatInputStepHeaderProps {
  icon?: ReactNode;
  label?: string;
  /** Custom left content (e.g. agent / project selectors); replaces icon + label. */
  children?: ReactNode;
  /** Step navigation is shown only when both are set. */
  step?: number;
  totalSteps?: number;
  onPrev?: () => void;
  onNext?: () => void;
  className?: string;
}

/**
 * Tab that sits behind the top edge of a ChatInput (Figma 8680:101472), e.g.
 * "Onboarding · 1 of 3" or the agent / project selectors (Figma 8988:201326).
 * Render it directly above the input; its bottom padding tucks under the input
 * card, so the card must be positioned (`relative`) to paint over it.
 */
export function ChatInputStepHeader({
  icon,
  label,
  children,
  step,
  totalSteps,
  onPrev,
  onNext,
  className,
}: Readonly<ChatInputStepHeaderProps>) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const labelTextClass = theme === 'dark' ? 'text-true-white' : 'text-grey-900';
  const stepTextClass = theme === 'dark' ? 'text-muted-foreground' : 'text-grey-400';
  const navButtonClass = cn(
    'inline-flex size-4 items-center justify-center text-grey-300 disabled:cursor-default disabled:opacity-50',
    theme === 'dark' ? 'enabled:hover:text-foreground' : 'enabled:hover:text-grey-900'
  );
  const hasSteps = step !== undefined && totalSteps !== undefined;

  return (
    <div
      className={cn(
        '-mb-6 mx-4 flex items-start justify-between rounded-t-2xl bg-grey-50 px-3 pb-7 pt-1.5 dark:bg-muted',
        className
      )}
    >
      {children ?? (
        <div className="flex items-center gap-2 opacity-80">
          {icon ? <span className="inline-flex size-5 shrink-0">{icon}</span> : null}
          <span className={cn('whitespace-nowrap text-sm font-medium leading-body', labelTextClass)}>
            {label}
          </span>
        </div>
      )}

      {hasSteps && (
        <div className="flex items-center gap-2 pt-0.5">
          <button
            type="button"
            aria-label={t('PREVIOUS', { defaultValue: 'Previous' })}
            className={navButtonClass}
            disabled={!onPrev || step <= 1}
            onClick={onPrev}
          >
            <ChevronLeft className="size-4" strokeWidth={1.5} />
          </button>
          <span className={cn('whitespace-nowrap text-sm leading-heading', stepTextClass)}>
            {t('STEP_OF_TOTAL', {
              defaultValue: '{{step}} of {{total}}',
              step,
              total: totalSteps,
            })}
          </span>
          <button
            type="button"
            aria-label={t('NEXT', { defaultValue: 'Next' })}
            className={navButtonClass}
            disabled={!onNext || step >= totalSteps}
            onClick={onNext}
          >
            <ChevronRight className="size-4" strokeWidth={1.5} />
          </button>
        </div>
      )}
    </div>
  );
}
